import React from 'react';
import { Divider } from '@mui/material';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  AI_COLORS, AI_FONTS,
  ImgSlot, TipBox, CheckItem,
  PageHero, ContentWrap, SectionTitle, SubTitle, Card, StepCircle,
} from '../shared';

import EscovaEletricaVideo from '../../../videos/escova-eletrica.mp4';
import img1 from '../../../imgs/adulto-idosos/1.png';
import img2 from '../../../imgs/adulto-idosos/2.png';
import img4 from '../../../imgs/adulto-idosos/4.png';
import img5 from '../../../imgs/adulto-idosos/5.png';
import img6 from '../../../imgs/adulto-idosos/6.png';
import img7 from '../../../imgs/adulto-idosos/7.png';
import img8 from '../../../imgs/adulto-idosos/8.png';
import img9 from '../../../imgs/adulto-idosos/9.png';
import img10 from '../../../imgs/adulto-idosos/10.png';
import img11 from '../../../imgs/adulto-idosos/11.png';
import img12 from '../../../imgs/adulto-idosos/12.png';
import img13 from '../../../imgs/adulto-idosos/13.png';
import img14 from '../../../imgs/adulto-idosos/14.png';
import img15 from '../../../imgs/adulto-idosos/15.png';
import img16 from '../../../imgs/adulto-idosos/16.png';
import img17 from '../../../imgs/adulto-idosos/17.png';



export default function HigienizacaoBucalAdulto() {
  const isMobile = useIsMobile();

  const tiposFio = [
    { tipo: 'Fio dental tradicional', desc: 'É o modelo mais comum e pode ser encontrado nas versões com ou sem cera.' },
    { tipo: 'Fita dental', desc: 'É mais larga e achatada, podendo ser mais confortável para algumas pessoas.' },
    { tipo: 'Passa-fio', desc: 'Ajuda a passar o fio dental em pessoas que usam aparelho ortodôntico, coroas ou implantes.' },
    { tipo: 'Escovas interdentais', desc: 'Podem ser indicadas em alguns casos, principalmente quando existem espaços maiores entre os dentes.' },
  ];

  const criteriosPasta = [
    {
      titulo: 'Ter flúor',
      texto: 'O flúor fortalece os dentes e ajuda a prevenir cáries. Procure pastas com cerca de 1.000 a 1.500 ppm de flúor no rótulo.',
    },
    {
      titulo: 'Ajudar a limpar sem desgastar',
      texto: 'A pasta deve remover a placa e restos de alimentos sem causar desgaste excessivo. Pastas com carvão ativado são proibidas, pois desgastam o esmalte dentário, causando sensibilidade!',
    },
    {
      titulo: 'Ter sabor agradável',
      texto: 'Um sabor confortável pode ajudar a manter o hábito diário da escovação.',
    },
    {
      titulo: 'Opções específicas quando necessário',
      texto: 'Pacientes com dentes sensíveis podem usar pastas que ajudam a diminuir a sensibilidade.',
    },
  ];

  const passosEscovacao = [
  {
    passo: '1º passo',
    desc: 'Insira na gengiva as cerdas da escova, fazendo movimentos vibratórios e de varredura nos dentes de cima e de baixo',
    img: img10,
    second: img11,
  },
  {
    passo: '2º passo',
    desc: 'Na região interna dos dentes, faça o movimento de “vassourinha” nos dentes de cima e de baixo',
    img: img12,
  },
  {
    passo: '3º passo',
    desc: 'Realize movimentos circulares na parte de fora dos dentes de cima e de baixo',
    img: img13,
    second: img14,
  },
  {
    passo: '4º passo',
    desc: 'Realize movimento de vai e vem na parte onde mordemos os alimentos nos dentes de cima e de baixo',
    img: img15,
  },
  {
    passo: '5º passo',
    desc: 'Escove a língua com movimentos de dentro para fora',
    img: img16,
  },
  {
    passo: '6º passo',
    desc: 'Não esqueça do fio dental! Passe o fio dental entre todos os dentes, fazendo movimentos suaves para remover os restos de alimentos e a placa que a escova não alcança.',
    img: img17,
  },
];

  return (
    <div style={{ fontFamily: AI_FONTS.body, background: 'transparent' }}>
      <PageHero
        title="Higienização Bucal"
        subtitle="Uma boa higiene pode te ajudar a evitar cáries, problemas na gengiva, mau hálito e outras doenças."
        audio="higienizacao-bucal.m4a"
      />

      <ContentWrap>

        {/* Como manter a boca saudável */}
        <SectionTitle style={{ marginTop: 0 }}>Como manter sua boca saudável?</SectionTitle>
        <ul style={{ padding: 0, margin: '0 0 8px' }}>
          <CheckItem>Escove os dentes pelo menos 3 vezes ao dia</CheckItem>
          <CheckItem>Use creme dental com flúor</CheckItem>
          <CheckItem>Escove também a língua</CheckItem>
          <CheckItem>Use fio dental diariamente</CheckItem>
          <CheckItem>Troque a escova a cada 3 meses ou quando estiver desgastada</CheckItem>
          <CheckItem>Visite o cirurgião dentista de 6 em 6 meses</CheckItem>
        </ul>

        <TipBox icon="ℹ️">
          Mesmo quem usa prótese ou tem poucos dentes precisa manter a higiene bucal diariamente.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Escova */}
        <SectionTitle>Como escolher a sua escova ideal?</SectionTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 14,
            marginBottom: 20,
          }}
        >
          {[
            { titulo: 'Cerdas macias', desc: 'Protegem a gengiva e o esmalte dos dentes' },
            { titulo: 'Pontas arredondadas e polidas', desc: 'Evitam machucados na gengiva' },
            { titulo: 'Cabeça pequena', desc: 'Facilita o acesso a todas as regiões da boca' },
          ].map((item, i) => (
            <Card key={i} variant={i}>
              <strong
                style={{
                  fontFamily: AI_FONTS.title,
                  color: AI_COLORS.blue900,
                  fontSize: '1.25rem',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                {item.titulo}
              </strong>
              <p style={{ margin: 0, fontSize: '0.93rem' }}>{item.desc}</p>
            </Card>
          ))}
        </div>

        <ImgSlot
          folder=""
          style={{ maxWidth: 360, margin: '0 auto' }}
          filename={img1}
          alt="Escova de dentes ideal: cerdas macias, pontas arredondadas e polidas, cabeça pequena"
          caption="Características da escova ideal para adultos e idosos"
        />

        <TipBox>
          Troque sua escova a cada 3 meses e também após gripe, infecção de garganta ou COVID-19.
          Se as cerdas estiverem abertas ou deformadas, faça a troca antes.
        </TipBox>

        {/* Escova elétrica */}
        <SectionTitle>E as escovas elétricas?</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          Se você tem dificuldade para movimentar as mãos ou realizar os movimentos da escovação,
          a escova elétrica pode tornar a limpeza dos dentes mais fácil e prática.
        </p>
        <p style={{ fontWeight: 600, color: AI_COLORS.blue900, marginBottom: 8 }}>
          Pode ser útil em casos de:
        </p>
        <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>Dores ou dificuldade motora nas mãos</li>
          <li>Tremores</li>
          <li>Dificuldade para controlar os movimentos</li>
          <li>Dificuldades após um AVC</li>
          <li>Alguma limitação física</li>
          <li>Diminuição da firmeza ou agilidade das mãos, algo comum com o avanço da idade</li>
        </ul>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            justifyContent: 'center',
            alignItems: 'flex-start',
            marginTop: 16,
          }}
        >
          <div style={{ flex: '1 1 320px', maxWidth: 420 }}>
            <ImgSlot
              filename={img2}
              alt="Escova elétrica de dentes"
              caption="A escova elétrica facilita a higiene bucal para quem tem dificuldades motoras"
              maxWidth={420}
            />
          </div>
          <figure style={{ flex: '1 1 320px', maxWidth: 420, margin: '20px auto' }}>
            <video
              controls
              playsInline
              style={{ width: '100%', borderRadius: 12, display: 'block' }}
            >
              <source src={EscovaEletricaVideo} type="video/mp4" />
              Seu navegador não suporta a tag de vídeo.
            </video>
            <figcaption
              style={{
                textAlign: 'center',
                fontSize: '0.9rem',
                color: AI_COLORS.blue800,
                marginTop: 8,
              }}
            >
              Veja como utilizar a escova elétrica
            </figcaption>
          </figure>
        </div>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Pasta */}
        <SectionTitle>Como escolher a sua pasta de dente ideal?</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          A pasta de dentes ideal ajuda a proteger os seus dentes e manter a sua boca saudável.
          Observe estas características:
        </p>

        {criteriosPasta.map((item, i) => (
          <Card key={i} variant={i}>
            <strong
              style={{
                fontFamily: AI_FONTS.title,
                color: AI_COLORS.blue900,
                fontSize: '1rem',
                display: 'block',
                marginBottom: 6,
              }}
            >
              ✔ {item.titulo}
            </strong>
            <p style={{ margin: 0, lineHeight: 1.6 }}>{item.texto}</p>
          </Card>
        ))}

        <SubTitle>Quanto de pasta de dente devo usar?</SubTitle>
        <p style={{ lineHeight: 1.75 }}>
          Para adultos e idosos, a quantidade recomendada é semelhante ao tamanho de um grão de ervilha.
        </p>

        <ImgSlot
          filename={img4}
          second={img5}
          alt="Quantidade ideal de pasta de dente — equivalente a um grão de ervilha"
          caption="Você não precisa encher toda a escova; a quantidade de um grão de ervilha é suficiente"
          maxWidth={360}
        />

        <TipBox>
          Usar mais pasta não significa que os dentes ficarão mais limpos. O mais importante é escovar
          corretamente e utilizar um creme dental com flúor.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Fio dental */}
        <SectionTitle>Fio dental: um passo importante para a sua saúde bucal</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          A escova limpa a parte da frente e de trás dos dentes, mas não consegue alcançar bem os
          espaços entre eles. O fio dental ajuda a remover restos de alimentos e bactérias dessas
          regiões, ajudando a prevenir cáries, mau hálito e problemas na gengiva.
        </p>
        <p style={{ lineHeight: 1.75 }}>
          O ideal é usar o fio dental <strong>todos os dias</strong> — você pode usar antes ou
          depois da escovação.
        </p>

        <SubTitle>Existem vários tipos de fio dental:</SubTitle>
        {tiposFio.map((item, i) => (
          <Card key={i} variant={i}>
            <strong
              style={{
                fontFamily: AI_FONTS.title,
                color: AI_COLORS.blue900,
                display: 'block',
                marginBottom: 4,
              }}
            >
              ✔ {item.tipo}
            </strong>
            <p style={{ margin: 0 }}>{item.desc}</p>
          </Card>
        ))}

        <ImgSlot
          filename={img6}
          maxWidth={230}
          alt="Tipos de fio dental: fio tradicional, fita dental, passa-fio e escovas interdentais"
          caption="Escolha o tipo de fio dental que você consegue usar com mais conforto e frequência"
        />

        <TipBox>
          O mais importante não é escolher o tipo "perfeito", e sim criar o hábito de usar
          diariamente. O melhor fio dental é aquele que você consegue usar com conforto e frequência.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Raspador de língua */}
        <SectionTitle>E o raspador de língua?</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          O raspador de língua é um instrumento usado para limpar a língua, ajudando a remover a
          saburra lingual — uma camada esbranquiçada formada por restos de alimentos e bactérias.
        </p>

        <p style={{ fontWeight: 600, color: AI_COLORS.blue900, marginBottom: 8 }}>
          A limpeza da língua pode ajudar a:
        </p>
        <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>Diminuir o mau hálito</li>
          <li>Reduzir o número de bactérias</li>
          <li>Melhorar a sensação de boca limpa</li>
          <li>Contribuir para a higiene bucal diária</li>
        </ul>

        <SubTitle>Como usar:</SubTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
          {[
            'Coloque o raspador na parte de trás da língua, sem forçar muito.',
            'Puxe suavemente em direção à ponta da língua.',
            'Lave o raspador após cada passada.',
            'Repita algumas vezes, até perceber a língua mais limpa.',
          ].map((passo, i) => (
            <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <StepCircle number={i + 1} />
              <p style={{ margin: 0, lineHeight: 1.65, paddingTop: 4 }}>{passo}</p>
            </div>
          ))}
        </div>

        <ImgSlot
          filename={img7}
          second={img8}
          alt="Raspador de língua e técnica correta de uso"
          maxWidth={380}
        />

        <TipBox>
          O raspador pode ser um complemento da higiene bucal, mas não substitui a escovação e o uso
          do fio dental. Essa etapa também pode ser realizada com a escova de dentes, fazendo
          movimentos de dentro para fora.
        </TipBox>

        <ImgSlot
          filename={img9}
          alt="Técnica de escovação da língua: movimentos de dentro para fora"
          caption="Escove a língua com movimentos de dentro pra fora"
          maxWidth={380}
        />

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Passo a passo */}
        <SectionTitle>Passo a passo da escovação dentária</SectionTitle>
        <TipBox icon="⏱️">
          Aguarde 20 minutos após a refeição para iniciar a escovação dos dentes.
        </TipBox>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
          {passosEscovacao.map((item, i) => (
            <React.Fragment key={i}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                background: i % 2 === 0 ? AI_COLORS.bgBlue50 : AI_COLORS.bgPurple50,
                borderRadius: 12,
                padding: '18px 22px',
              }}
            >
              {/* Texto por cima */}
              <div>
                <strong
                  style={{
                    fontFamily: AI_FONTS.title,
                    color: AI_COLORS.blue900,
                    fontSize: '1.1rem',
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  {item.passo}
                </strong>
                <p style={{ margin: 0, lineHeight: 1.65 }}>{item.desc}</p>
              </div>

              {/* Imagens abaixo, com seta entre elas */}
              {item.img && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                  }}
                >
                  <ImgSlot
                    filename={item.img}
                    alt={`${item.passo} da escovação`}
                    maxWidth={280}
                  />
                  {item.second && (
                    <>
                      <div
                        aria-hidden="true"
                        style={{
                          color: AI_COLORS.blue800,
                          fontSize: '2rem',
                          lineHeight: 1,
                          fontWeight: 700,
                        }}
                      >
                        →
                      </div>
                      <ImgSlot
                        filename={item.second}
                        alt={`${item.passo} da escovação (continuação)`}
                        maxWidth={280}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {i < passosEscovacao.length - 1 && (
              <div
                aria-hidden="true"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: AI_COLORS.blue800,
                  fontSize: '1.8rem',
                  lineHeight: 1,
                  margin: '-4px 0',
                }}
              >
                ↓
              </div>
            )}
            </React.Fragment>
          ))}
        </div>

        <TipBox icon="⚠️">
          Não esqueça do fio dental! A escovação e o fio dental juntos protegem todas as
          superfícies dos dentes.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <h3
            style={{
              fontFamily: AI_FONTS.title,
              color: AI_COLORS.blue800,
              marginBottom: 16,
            }}
          >
            Assista: Higiene bucal — passo a passo da escovação e uso do fio dental
          </h3>
          <iframe
            width={isMobile ? '100%' : '560'}
            height="315"
            src="https://www.youtube.com/embed/4dbwGxxX2RM"
            title="Higiene bucal para idosos"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ borderRadius: 12, maxWidth: '100%' }}
          />
        </div>
      </ContentWrap>
    </div>
  );
}
